﻿import React, { useState } from 'react';
import { ColumnSearchRequest } from '../../models/Common/column-search-request';
import { ColumnHeader } from '../../models/column-header';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType } from '../../models/enums';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Item as ToolbarItem,
    Paging,
    Pager,
    HeaderFilter,
    FilterRow,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import RadioGroup from 'devextreme-react/radio-group';
import Button from 'devextreme-react/button';

const technologyTypes = [
    { id: BenchSheetTechnologyType.GasChromatography, name: "GC" },
    { id: BenchSheetTechnologyType.HighPerformanceLiquidChromatography, name: "HPLC" }
];

export interface Props {
    defaultTechnology?: BenchSheetTechnologyType;
    onApplySelection?: (columnHeader: ColumnHeader) => void;
    onCancel?: () => void;
}

export default function ColumnSearchComponent(props: Props) {
    const [columns, setColumns] =
        useState<ColumnHeader[]>();

    const [selectedColumn, setSelectedColumn] =
        useState<ColumnHeader>();

    const [technologyTypeFilter, setTechnologyTypeFilter] =
        useState<number>(props.defaultTechnology ? props.defaultTechnology : BenchSheetTechnologyType.GasChromatography);

    const [columnIdFilter, setColumnIdFilter] =
        useState<number>();

    const [descriptionFilter, setDescriptionFilter] =
        useState<string>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    return (
        <div>
            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Technology</div>
                    <RadioGroup id="technology" items={technologyTypes}
                        displayExpr='name' valueExpr='id' layout="horizontal"
                        value={technologyTypeFilter ? technologyTypeFilter : 1}
                        onValueChanged={(e) => setTechnologyTypeFilter(e.value)} />
                </div>

                <div className="pw-form-unit pw-comp-10">
                    <label className="textbox-label">Column ID</label>
                    <NumberBox id="columnId" className="textbox"
                        onValueChanged={columnIdFilterValueChanged}
                        showClearButton={true}
                        placeholder="column id" value={columnIdFilter ? columnIdFilter : undefined} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <label className="textbox-label">Description</label>
                    <TextBox id="description" className="textbox"
                        showClearButton={true}
                        onValueChanged={descriptionFilterValueChanged}
                        placeholder="description" value={descriptionFilter ? descriptionFilter : undefined} />
                </div>

                <Button id="searchButton" text="Search" icon="find" type="default"
                    className="pw-button-right-labeled-textbox"
                    hint="search"
                    height='1.6rem'
                    onClick={onSearch} />
            </div>

            <div className="pw-comp-row">
                <DataGrid
                    height={"29rem"}
                    dataSource={columns ? columns : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='widget'
                    keyExpr="columnHeaderId"
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    onSelectionChanged={columnSelectionChanged}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <Paging defaultPageSize={30} />
                    <Pager
                        showPageSizeSelector={true}
                        allowedPageSizes={[30, 60, 100]}
                        showInfo={true} />
                    <SearchPanel visible={true} />

                    <Column dataField='columnId' caption="Column ID" width='7rem' />
                    <Column dataField='vendorName' caption="Vendor" width='10rem' />
                    <Column dataField='manufacturerName' caption="Manufacturer" width='10rem' />
                    <Column dataField='dimensions' width='20rem' />
                    <Column dataField='vendorPartNumber' caption="Vendor Part#" width='9rem' />
                    <Column dataField='description' />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Columns</div>
                            </div>
                        </ToolbarItem>
                        <ToolbarItem location='after' name="searchPanel" />

                    </Toolbar>
                </DataGrid>
            </div>

            <div className="pw-comp-row">
                <div className="pw-centered-buttons-container">
                    <Button id="applySelectionButton"
                        className="pw-button"
                        text="Apply" type="default"
                        hint="apply selection"
                        height={'2.6em'}
                        onClick={onApplyClick} />

                    <Button id="cancelButton"
                        className="pw-button"
                        text="Cancel" type="normal"
                        hint="cancel"
                        height={'2.6em'}
                        onClick={onCancelClick} />
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function columnIdFilterValueChanged(e: any) {
        setColumnIdFilter(e.value);
    }

    function descriptionFilterValueChanged(e: any) {
        setDescriptionFilter(e.value);
    }

    function onApplyClick(e: any) {
        if (selectedColumn && props.onApplySelection)
            props.onApplySelection(selectedColumn);
    }

    function onCancelClick(e: any) {
        if (props.onCancel)
            props.onCancel();
    }

    function columnSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedColumn(e.selectedRowsData[0]);
        }
    }

    function onSearch(e: any) {
        let request: ColumnSearchRequest = {
            columnId: columnIdFilter,
            description: descriptionFilter,
            technologyId: technologyTypeFilter
        }




        getColumns(request);
    }

    async function getColumns(request: ColumnSearchRequest) {
        await CrudFunctions.postData("common/columnheaderssearch", request)
            .then((data) => {
                setColumns(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Columns");
            });
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}

